import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { Guid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	IFrame,
	IFramePermission,
	PopoutableContent,
	Portal
} from "client/Components";
import { hasStackingContextIssues } from "client_legacy/ndaw/mobile";
import { LiveTileContent } from "../LiveTileContent";
import { TileTabProps } from "../TileTabSelector";
import styles from "./YammerLiveTileContent.module.less";
import {
	base64UrlEncode,
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { useEffectiveTheme } from "client/Hooks";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { TileContext } from "../TileContext";
import { YammerFeedKind, YammerTabDisplayModel } from "client/Api/Workspace/workspace";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type YammerLiveTileContentProps = {
	tabs: readonly YammerTabDisplayModel[];
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const YammerLiveTileContent = (props: YammerLiveTileContentProps): ReactElement => {
	const {
		tabs,
		onOpenTileMenu
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);
	const [isPoppedOut, setIsPoppedOut] = useState<boolean>(false);

	const {
		id: tileId,
		icon,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		// We leave the yammer tile content "visible" in edit mode so it doesn't handle yammer's responsive
		// code incorrect (i.e. width < responsive breakpoint, because the tile width is 0 when hidden, so hide the
		// the avatar).
		shouldNotHideContent: true,
		appIsLicensed,
		appIsNotAccessible
	});

	const useSeparateDomForPopout: boolean = useMemo(
		() => hasStackingContextIssues(),
		[]
	);

	const tabProps = useMemo(
		(): readonly TileTabProps[] => {
			return tabs.map(x => {
				const result: TileTabProps = {
					uniqueId: x.uniqueId,
					name: x.name
				};

				return result;
			});
		},
		[tabs]
	);

	const activeTab = useMemo(
		(): YammerTabDisplayModel | undefined => {
			if (!activeTabId) {
				return;
			}
			return tabs.find(x => x.uniqueId === activeTabId);
		},
		[activeTabId, tabs]
	);

	const popOutHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			setIsPoppedOut(true);
		},
		[addAnalyticsEvent]
	);

	const popInHandler = useCallback(
		(): void => setIsPoppedOut(false),
		[]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			enlarge: () => popOutHandler()
		}),
		[popOutHandler]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			canBeEnlarged: isNotNil(activeTab)
		}),
		[activeTab]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			if (!activeTab) {
				return;
			}

			const url: string = getNavigationUrl(activeTab);
			navigateTo(url, true);
		},
		[activeTab, addAnalyticsEvent]
	);

	return (
		<>
			<LiveTileContent
				name={name}
				icon={icon}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
				isPoppedOut={isPoppedOut}
			>
				<LiveTileContent.Header
					icon={icon}
					actions={defaultActions}
					actionContext={actionContext}
				>
					<LiveTileContent.TabSelector
						value={activeTabId}
						onChange={setActiveTabId}
						tabs={tabProps}
						tileId={tileId}
						tileName={name}
					/>
				</LiveTileContent.Header>
				<LiveTileContent.Content>
					{activeTab && (
						<PopoutableContent
							isPoppedOut={!useSeparateDomForPopout && isPoppedOut}
							onPopin={popInHandler}
							title={name}
						>
							<YammerEmbed activeTab={activeTab} />
						</PopoutableContent>
					)}
				</LiveTileContent.Content>
				{activeTab && (
					<LiveTileContent.Footer
						name={name}
						onClick={navigateHandler}
					/>
				)}
			</LiveTileContent>

			{activeTab && useSeparateDomForPopout && isPoppedOut && (
				<Portal container={document.body}>
					<PopoutableContent
						isPoppedOut
						onPopin={popInHandler}
						title={name}
					>
						<YammerEmbed activeTab={activeTab} />
					</PopoutableContent>
				</Portal>
			)}
		</>
	);
};
YammerLiveTileContent.displayName = "YammerLiveTileContent";

type YammerEmbedProps = {
	activeTab: YammerTabDisplayModel;
};

const YammerEmbed = (props: YammerEmbedProps): ReactElement => {
	const {
		activeTab
	} = props;

	const isDarkTheme: boolean = useEffectiveTheme() === Theme.Dark;

	const url = useMemo(
		(): string => createYammerEmbedUrl(activeTab, isDarkTheme),
		[activeTab, isDarkTheme]
	);

	return (
		<div className={styles.hostContainer}>
			<IFrame
				src={url}
				className={styles.iframe}
				permissions={yammerAppIframePermissions}
				trackingId="yammer-tile-iframe"
			/>
		</div>
	);
};
YammerEmbed.displayName = "YammerEmbed";

function createYammerEmbedUrl(
	model: YammerTabDisplayModel,
	isDarkTheme: boolean
): string {
	let result: string = "https://web.yammer.com/embed/";

	switch (model.feedKind) {
		case YammerFeedKind.AllCompany:
			result += `groups/${getGroupId(0)}`;
			break;
		case YammerFeedKind.OtherGroup:
			result += `groups/${getGroupId(ensureNotNil(model.feedId))}`;
			break;
		case YammerFeedKind.Home:
			result += "feed";
			break;
		default:
			never(model.feedKind);
	}

	const theme: string = isDarkTheme
		? "dark"
		: "light";

	const queryString: string = [
		"header=false",
		"footer=false",
		`theme=${theme}`,
		"includeFeedInformation=false"
	].join("&");

	return result + `?${queryString}`;
};

function getGroupId(feedId: number): string {
	const json = JSON.stringify({
		"_type": "Group",
		id: feedId.toString()
	});

	return base64UrlEncode(json, true);
};

const YAMMER_URL = "https://www.yammer.com";

function getNavigationUrl(tab: YammerTabDisplayModel): string {
	switch (tab.feedKind) {
		case YammerFeedKind.AllCompany:
			return `${YAMMER_URL}/#/threads/company?type=general&view=all`;
		case YammerFeedKind.OtherGroup:
			return `${YAMMER_URL}/#/threads/inGroup?type=in_group&feedId=${tab.feedId}`;
		case YammerFeedKind.Home:
			return YAMMER_URL;
		default:
			never(tab.feedKind);
	}
}

const yammerAppIframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];

type ActionContext = {
	canBeEnlarged: boolean;
};

type ActionHandlers = {
	enlarge: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "enlarge",
			title: localize("_Enlarge"),
			icon: "arrow-up-right-from-square",
			canExecute: (x) => x.canBeEnlarged,
			execute: () => handlers.enlarge()
		}
	];
}
